import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import type { StoryGroup } from "Components/Stories/StoriesSection";
import { StoryAvatar } from "Components/Stories/StoryAvatar";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { Resources, useResource } from "Translations/Resources";
import { getProfilePhotoUrl } from "Utils/Image/getProfilePhotoUrl";

type Props = {
  /** The current persona's own story group from the live feed, when they have live stories. */
  ownGroup?: StoryGroup;
  /** Active persona may post stories (Admin/Merchant) — shows the (disabled) plus badge. */
  canPost: boolean;
  onOpenGroup: (actorId: string) => void;
};

const PlusBadge = () => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: "50%",
      bgcolor: "primary.main",
      color: "primary.contrastText",
      border: "2px solid",
      borderColor: "background.paper",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <AddIcon sx={{ fontSize: 16 }} />
  </Box>
);

/**
 * The bar's first circle — the current persona's own story (Instagram-style). With live own
 * stories it opens them in the viewer (feed-enriched avatar + watched ring, so the author sees
 * exactly what viewers see); without them it shows the local profile photo/initials ring-less.
 * The empty circle is a PLACEHOLDER for the upcoming self-serve publish flow: posting-capable
 * personas see the plus badge, but the whole circle renders disabled (dimmed, non-interactive)
 * until that flow ships.
 */
export const OwnStoryAvatar = ({ ownGroup, canPost, onOpenGroup }: Props) => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const label = t(Resources.Stories.Bar.YourStory);

  if (ownGroup) {
    return (
      <StoryAvatar
        name={label}
        imageUrl={ownGroup.actorImage}
        authorKind={ownGroup.authorKind}
        ringVariant={ownGroup.isRead ? "read" : "unread"}
        onClick={() => onOpenGroup(ownGroup.actorId)}
      />
    );
  }

  const initials =
    `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase() ||
    undefined;

  return (
    // Disabled placeholder: dimmed and without onClick (StoryAvatar then renders a
    // non-interactive element, so it is skipped by keyboard/screen-reader focus too).
    <Box sx={{ opacity: 0.45 }}>
      <StoryAvatar
        name={label}
        imageUrl={getProfilePhotoUrl(user?.userPublicID, user?.profilePicture)}
        // The local avatar is a person photo even for personas whose published stories present as
        // the Mo.one brand — the brand crescent appears only once an own group exists.
        authorKind="merchant"
        ringVariant="none"
        avatarContent={initials}
        badge={canPost ? <PlusBadge /> : undefined}
      />
    </Box>
  );
};
